import React, { useState } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { useLocation } from 'react-router-dom';

const WhatsAppFloat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { settings, openChat } = useWhatsApp();
  const location = useLocation();

  if (!settings.enabled) return null;

  const getPageContext = () => {
    const path = location.pathname;
    if (path.startsWith('/services/')) return { page: 'service-detail', context: path };
    if (path.startsWith('/blog/')) return { page: 'blog-post', context: path };
    if (path === '/blog') return { page: 'blog', context: '' };
    if (path === '/portfolio') return { page: 'portfolio', context: '' };
    if (path === '/admin') return { page: 'admin', context: '' };
    return { page: 'homepage', context: '' };
  };

  const handleStartChat = () => {
    const ctx = getPageContext();
    openChat({ ...ctx, customMessage: settings.defaultMessage });
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50" style={{ zIndex: 9999 }}>
        {isOpen && (
          <div
            className="absolute bottom-16 right-0 w-72 sm:w-80 bg-white rounded-2xl shadow-2xl overflow-hidden mb-4"
            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.15)', animation: 'waSlideUp 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="p-4 text-white" style={{ backgroundColor: '#075E54' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#25D366' }}>
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base" data-testid="whatsapp-contact-name">{settings.contactName}</h4>
                    <p className="text-xs text-green-100">{settings.companyLabel}</p>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded-full transition-colors" aria-label="Close chat">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-4" style={{ backgroundColor: '#E5DDD5' }}>
              <div className="bg-white p-3 rounded-lg shadow-sm max-w-[85%] relative">
                <p className="text-sm text-gray-700" data-testid="whatsapp-greeting">{settings.greetingMessage}</p>
                <span className="text-[10px] text-gray-400 float-right mt-1">
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <div className="absolute -left-2 top-3 w-0 h-0" style={{ borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderRight: '8px solid white' }} />
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-white border-t">
              <button
                onClick={handleStartChat}
                className="w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90 active:scale-95"
                style={{ backgroundColor: '#25D366' }}
                data-testid="whatsapp-start-chat"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Start Chat
              </button>
              <p className="text-xs text-gray-500 text-center mt-2" data-testid="whatsapp-response-time">{settings.responseTime}</p>
            </div>
          </div>
        )}

        {/* Main Floating Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="group relative w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
          style={{ backgroundColor: '#25D366', boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)' }}
          aria-label="Chat with us on WhatsApp"
          data-testid="whatsapp-float-button"
        >
          <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ backgroundColor: '#25D366' }} />
          {isOpen ? (
            <X className="w-6 h-6 sm:w-7 sm:h-7 text-white transition-transform duration-200" />
          ) : (
            <svg viewBox="0 0 24 24" className="w-7 h-7 sm:w-8 sm:h-8 fill-white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          )}
          <span className="hidden sm:block absolute right-full mr-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            Chat with us!
            <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full border-8 border-transparent border-l-gray-900" />
          </span>
        </button>
      </div>

      <style>{`
        @keyframes waSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default WhatsAppFloat;
